import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth/use-user';
import { toast } from '@/hooks/errors/use-toast';
import { updateUser } from '@/hooks/auth/database-users';
import { convertToWebP, isConvertibleToWebP, getOptimalConversionSettings } from '@/utils/image-conversion';

export type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

export interface UseProfilePhotoReturn {
  // Upload functions
  uploadProfilePhoto: (file: File) => Promise<string>;
  removeProfilePhoto: () => Promise<void>;
  
  // Upload state
  uploadStatus: UploadStatus;
  uploadProgress: number;
  errorMessage: string | null;
}

/**
 * Hook for managing profile photo uploads with progress tracking
 * 
 * @returns Functions and state for profile photo management
 */
export const useProfilePhoto = (): UseProfilePhotoReturn => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * Invalidate user profile cache after photo changes
   */
  const invalidateUserProfile = async () => {
    if (!user?.id) return;
    
    // Invalidate the new user profile query
    await queryClient.invalidateQueries({ queryKey: ['user-profile', user.id] });
    await queryClient.refetchQueries({ queryKey: ['user-profile', user.id] });
  };

  /**
   * Upload a profile photo file
   */
  const uploadProfilePhoto = async (file: File): Promise<string> => {
    if (!user?.id) {
      throw new Error('User must be authenticated to upload profile photo');
    }

    try {
      setUploadStatus('uploading');
      setUploadProgress(0);
      setErrorMessage(null);

      // 🚀 PHASE 1: AUTOMATIC WEBP CONVERSION
      let processedFile = file;
      let conversionInfo: string | null = null;

      // Convert to WebP if it's a convertible format and not already WebP
      if (file.type !== 'image/webp' && isConvertibleToWebP(file)) {
        try {
          console.log('[ProfilePhoto] Converting to WebP:', file.name);
          
          const conversionSettings = getOptimalConversionSettings(file, 'profile');
          const result = await convertToWebP(file, conversionSettings);
          
          processedFile = result.file;
          conversionInfo = `Converted from ${result.originalFormat} to WebP (${result.compressionRatio.toFixed(1)}% size reduction)`;
          
          console.log('[ProfilePhoto] WebP conversion successful:', {
            originalSize: result.originalSize,
            newSize: result.newSize,
            savings: `${result.compressionRatio.toFixed(1)}%`
          });
        } catch (error) {
          console.warn('[ProfilePhoto] WebP conversion failed, using original:', error);
          // Continue with original file if conversion fails
        }
      } else if (file.type === 'image/webp') {
        console.log('[ProfilePhoto] File already in WebP format');
      } else {
        console.log('[ProfilePhoto] File type not convertible to WebP, using original');
      }

      setUploadProgress(10);

      // Generate unique filename with proper extension
      const extension = processedFile.type === 'image/webp' ? 'webp' : (processedFile.name.split('.').pop() || 'jpg');
      const fileName = `${user.id}-${Date.now()}.${extension}`;
      const filePath = `${user.id}/${fileName}`;

      setUploadProgress(25);

      // Upload to Supabase Storage (now with WebP file if conversion succeeded)
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile_pictures')
        .upload(filePath, processedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      setUploadProgress(50);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile_pictures')
        .getPublicUrl(filePath);

      setUploadProgress(75);

      // Update user profile with new photo URL
      const { error: updateError } = await updateUser(user.id, {
        profile_picture_url: publicUrl
      });

      if (updateError) {
        throw new Error(`Profile update failed: ${updateError.message}`);
      }

      setUploadProgress(100);
      setUploadStatus('success');

      // Invalidate cache and dispatch event
      await invalidateUserProfile();
      
      const event = new CustomEvent('profile-picture-updated', {
        detail: { url: publicUrl }
      });
      document.dispatchEvent(event);

      // Show success toast with conversion info
      const baseMessage = "Your profile photo has been uploaded successfully.";
      const description = conversionInfo ? `${baseMessage} ${conversionInfo}` : baseMessage;
      
      toast({
        title: "Profile photo updated",
        description,
        variant: "success"
      });

      return publicUrl;

    } catch (error) {
      console.error('Profile photo upload error:', error);
      setUploadStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Upload failed');
      
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload profile photo",
        variant: "error"
      });
      
      throw error;
    }
  };

  /**
   * Remove the current profile photo
   */
  const removeProfilePhoto = async (): Promise<void> => {
    if (!user?.id) {
      throw new Error('User must be authenticated to remove profile photo');
    }

    try {
      setUploadStatus('uploading');
      setErrorMessage(null);

      // Update user profile to remove photo URL
      const { error: updateError } = await updateUser(user.id, {
        profile_picture_url: null
      });

      if (updateError) {
        throw new Error(`Profile update failed: ${updateError.message}`);
      }

      setUploadStatus('success');

      // Invalidate cache and dispatch event
      await invalidateUserProfile();
      
      const event = new CustomEvent('profile-picture-updated', {
        detail: { url: null }
      });
      document.dispatchEvent(event);

      toast({
        title: "Profile photo removed",
        description: "Your profile photo has been removed successfully.",
        variant: "success"
      });

    } catch (error) {
      console.error('Profile photo removal error:', error);
      setUploadStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Removal failed');
      
      toast({
        title: "Removal failed",
        description: error instanceof Error ? error.message : "Failed to remove profile photo",
        variant: "error"
      });
      
      throw error;
    }
  };

  return {
    uploadProfilePhoto,
    removeProfilePhoto,
    uploadStatus,
    uploadProgress,
    errorMessage
  };
};
